import { IOnboardingMe, OnboardingWorkflow } from '../interfaces/onboarding.interface';

/**
 * Rekenwerk achter de onboarding (docs/ONBOARDING.md), los van Angular zodat
 * het in test:rounds mee kan.
 */

/** De workflows waarvoor deze app een rondleiding heeft. */
export const BEKENDE_WORKFLOWS: readonly OnboardingWorkflow[] = ['upload', 'plannen', 'taggen', 'playlists'];

export interface IAanDeSlagTaak {
  sleutel: OnboardingWorkflow;
  minuten: number;
  videoUrl: string | null;
  klaar: boolean;
}

/**
 * De taken op de kaart: de workflows van het gekozen profiel, in de volgorde
 * die de beheerder gaf. Een sleutel die deze app (nog) niet kent, valt weg:
 * daar is geen titel en geen rondleiding voor.
 */
export function aanDeSlagTaken(staat: IOnboardingMe | null): IAanDeSlagTaak[] {
  if (!staat?.profiel) {
    return [];
  }
  const profiel = (staat.profielen ?? []).find(p => p.sleutel === staat.profiel);
  const klaar = new Set(staat.klaar ?? []);
  const gezien = new Set<string>();
  return (profiel?.workflows ?? [])
    .filter((w): w is OnboardingWorkflow => (BEKENDE_WORKFLOWS as readonly string[]).includes(w))
    .filter(w => !gezien.has(w) && !!gezien.add(w))
    .map(sleutel => {
      const instelling = (staat.workflows ?? []).find(w => w.sleutel === sleutel);
      return {
        sleutel,
        minuten: instelling?.minuten ?? 0,
        videoUrl: veiligeVideoUrl(instelling?.videoUrl),
        klaar: klaar.has(sleutel),
      };
    });
}

/**
 * Staat de kaart "Getting started" op de startpagina? Niet als hij verborgen
 * is, niet zonder taken, en niet voor een bestaande gebruiker die er nooit
 * aan begonnen is (die krijgt hem pas via Help).
 */
export function aanDeSlagZichtbaar(staat: IOnboardingMe | null): boolean {
  if (!staat || staat.verborgen) {
    return false;
  }
  if (staat.status === 'bestaand' && !staat.profiel && !(staat.klaar?.length)) {
    return false;
  }
  return aanDeSlagTaken(staat).length > 0;
}

/** Heeft de gebruiker de vragen ooit beantwoord? */
export function onboardingBeantwoord(staat: IOnboardingMe | null): boolean {
  return staat?.status === 'ingevuld' && !!staat.profiel;
}

export type WelkomStap = 'profiel' | 'doelen' | 'team';

/** Stap 3 (team) valt weg bij één team of minder; dan gaat dat ene team mee. */
export function welkomStappen(teams: readonly string[]): WelkomStap[] {
  return teams.length > 1 ? ['profiel', 'doelen', 'team'] : ['profiel', 'doelen'];
}

/** Alleen een https-link openen we in een nieuw tabblad; de rest is geen video. */
export function veiligeVideoUrl(url: string | null | undefined): string | null {
  const schoon = (url ?? '').trim();
  return /^https:\/\//i.test(schoon) ? schoon : null;
}

export interface IRechthoek { top: number; left: number; width: number; height: number; }

export interface IBallonPlek {
  top: number;
  left: number;
  /** Waar de ballon staat ten opzichte van het doel; `midden` = geen doel. */
  kant: 'onder' | 'boven' | 'midden';
  /** Horizontale plek van het pijltje, gemeten vanaf de linkerrand van de ballon. */
  pijl: number;
}

/**
 * Waar de ballon komt. Onder het doel als dat past, anders erboven; nooit
 * buiten het venster (16 px marge). Zonder doel midden onderin.
 */
export function ballonPlek(doel: IRechthoek | null, ballon: { width: number; height: number },
                           venster: { width: number; height: number }, afstand = 14, marge = 16): IBallonPlek {
  const maxLinks = Math.max(marge, venster.width - ballon.width - marge);
  if (!doel) {
    return {
      top: Math.max(marge, venster.height - ballon.height - marge * 6),
      left: Math.min(maxLinks, Math.max(marge, (venster.width - ballon.width) / 2)),
      kant: 'midden',
      pijl: 0,
    };
  }
  const midden = doel.left + doel.width / 2;
  const left = Math.min(maxLinks, Math.max(marge, midden - ballon.width / 2));
  const pijl = Math.min(ballon.width - 22, Math.max(22, midden - left));
  const onder = doel.top + doel.height + afstand;
  const boven = doel.top - afstand - ballon.height;
  let kant: 'onder' | 'boven' = 'onder';
  let top = onder;
  if (onder + ballon.height > venster.height - marge && boven >= marge) {
    kant = 'boven';
    top = boven;
  }
  top = Math.min(Math.max(marge, top), Math.max(marge, venster.height - ballon.height - marge));
  return { top, left, kant, pijl };
}
